import { useCallback, useEffect, useMemo, useState } from "react";
import qs from "query-string";

import { FiltersEnum, SORT_ENUM, getParamItems } from "../../components/Filters/Filters.helpers";
import { DocumentTypesEnum } from "../../core/enums/PublicationTypesEnum";
import { getQueryString } from "../../core/api";
import { History, Location } from "history";
import { MAC_CATEGORY_TITLE } from "../../core/api";

interface Params {
    inMyLibrary: boolean;
    isSearchOpened: boolean;
    location: Location;
    history: History;
    ribbons: any[];
}

type BrowseFilters = {
    ribbonsIds: number[];
    categoriesIds: number[];
    subscriptionPackageIds: number[];
    tagsIds: number[];
    orderBy: string;
    sortOrder: string;
    hasAccess: boolean;
    types: DocumentTypesEnum[];
    searchQuery: string;
    packageIds: number[];
};

// Helper type for internal patch with automation flag
type Patch = Partial<BrowseFilters>;

export default function useBrowseFilters({
    inMyLibrary,
    isSearchOpened,
    location,
    history,
    ribbons
}: Params) {
    /* -------------------------------------------------
     * Initialization
     * -------------------------------------------------*/
    const initializeFilters = (): BrowseFilters => {
        const queryParams = qs.parse(location.search.replace(/^\?/, ""));

        const ribbonsIds = getParamItems(queryParams[FiltersEnum.ribbonsIds]);
        const tagsIds = getParamItems(queryParams[FiltersEnum.tags]);
        const subscriptionPackageIds = getParamItems(queryParams[FiltersEnum.packages]);
        const orderBy = (queryParams[FiltersEnum.orderBy] as string) || SORT_ENUM.DATE;
        const sortOrder = (queryParams[FiltersEnum.sortOrder] as string) || SORT_ENUM.DESC;
        const searchQuery = (queryParams[FiltersEnum.searchText] as string) || "";

        const savedNewRelease = localStorage.getItem("library:isNewRelease");
        if (savedNewRelease === "true" && !ribbonsIds.includes(18)) ribbonsIds.push(18);

        return {
            ribbonsIds,
            categoriesIds: [],
            subscriptionPackageIds,
            tagsIds,
            orderBy,
            sortOrder,
            hasAccess: inMyLibrary,
            types: [DocumentTypesEnum.Publication],
            searchQuery,
            packageIds: []
        };
    };

    const [browseFilters, setBrowseFilters] = useState<BrowseFilters>(initializeFilters);

    const [isNewRelease, setIsNewRelease] = useState<boolean>(() => {
        const savedNewRelease = localStorage.getItem("library:isNewRelease");
        return savedNewRelease === "true";
    });

    // Track whether the user explicitly selected a type. If true we do NOT auto-override it.
    const [isTypeExplicit, setIsTypeExplicit] = useState(false);

    /* -------------------------------------------------
     * Derived data
     * -------------------------------------------------*/
    const macRibbonId = useMemo(() => {
        return ribbons?.find((r) => (r as any).isMacRibbon || r.title === MAC_CATEGORY_TITLE)?.id as number | undefined;
    }, [ribbons]);

    /* -------------------------------------------------
     * Update helpers
     * -------------------------------------------------*/
    const applyPatch = useCallback(
        (patch: Patch, isAutoUpdate = false) => {
            if (patch.types !== undefined) {
                setIsTypeExplicit(!isAutoUpdate); // explicit only when coming from user
            }
            // we will evaluate ribbon changes inside state update where we have previous value

            setBrowseFilters((prev) => {
                let nextPatch = { ...patch } as Partial<BrowseFilters>;

                // New Releases ribbon logic
                if (nextPatch.ribbonsIds?.includes(18)) {
                    setIsNewRelease(true);
                    if (!nextPatch.sortOrder && !prev.ribbonsIds.includes(18)) {
                        nextPatch.sortOrder = SORT_ENUM.DESC;
                    }
                } else if (nextPatch.ribbonsIds !== undefined && !nextPatch.ribbonsIds.includes(18)) {
                    setIsNewRelease(false);
                }

                if (nextPatch.ribbonsIds?.length === 0) {
                    setIsNewRelease(false);
                    localStorage.removeItem("library:isNewRelease");
                }

                // MAC ribbon specific logic
                if (nextPatch.ribbonsIds && macRibbonId) {
                    const includesMacRibbon = nextPatch.ribbonsIds.includes(macRibbonId);
                    const previouslyIncludedMac = prev.ribbonsIds.includes(macRibbonId);

                    const isPatchTypesExplicit = Object.prototype.hasOwnProperty.call(nextPatch, "types");
                    const isDefaultPublication = (arr: any) =>
                        Array.isArray(arr) && arr.length === 1 && arr[0] === DocumentTypesEnum.Publication;

                    if (includesMacRibbon && !previouslyIncludedMac) {
                        // Entering MAC ribbon: force MAC type irrespective of previous selection
                        nextPatch.types = [DocumentTypesEnum.Mac];
                        setIsTypeExplicit(false);
                        nextPatch.orderBy = "updateDate";
                        if (!nextPatch.tagsIds) nextPatch.tagsIds = [];
                        if (!nextPatch.subscriptionPackageIds) nextPatch.subscriptionPackageIds = [];
                    }

                    if (!includesMacRibbon && previouslyIncludedMac) {
                        // Moving away from MAC ribbon: always switch back to Publication type
                        nextPatch.types = [DocumentTypesEnum.Publication];
                        if (!nextPatch.orderBy) nextPatch.orderBy = SORT_ENUM.DATE;
                    }
                }

                // Clear explicit flag when ribbons set changes
                if (nextPatch.ribbonsIds && nextPatch.ribbonsIds.toString() !== prev.ribbonsIds.toString()) {
                    setIsTypeExplicit(false);
                }

                // Maintain explicit flag based on direct type modifications
                if (nextPatch.types) {
                    setIsTypeExplicit(true);
                }

                // If MAC ribbon is no longer selected AND user didn't explicitly modify types in this patch,
                // reset type back to Publication when it was implicitly set to MAC.
                if (macRibbonId) {
                    const ribbonsAfter = nextPatch.ribbonsIds ?? prev.ribbonsIds;
                    const macStillSelected = ribbonsAfter.includes(macRibbonId);

                    const typeWasInPatch = Object.prototype.hasOwnProperty.call(patch, 'types');
                    const currentTypes = (nextPatch.types ?? prev.types);

                    const isMacOnly = currentTypes.length === 1 && currentTypes[0] === DocumentTypesEnum.Mac;

                    if (!macStillSelected && isMacOnly && !typeWasInPatch) {
                        nextPatch.types = [DocumentTypesEnum.Publication];
                        if (!Object.prototype.hasOwnProperty.call(nextPatch, 'orderBy')) {
                            nextPatch.orderBy = SORT_ENUM.DATE;
                        }
                        // Since this is automatic, keep isTypeExplicit false
                    }
                }

                return {
                    ...prev,
                    ...nextPatch,
                    orderBy: nextPatch.orderBy ?? prev.orderBy,
                    sortOrder: nextPatch.sortOrder ?? prev.sortOrder
                };
            });
        },
        [macRibbonId]
    );

    // Public updater (user driven)
    const updateBrowseFilters = useCallback((patch: Patch) => applyPatch(patch, false), [applyPatch]);

    /* -------------------------------------------------
     * Side-effects
     * -------------------------------------------------*/
    // Persist new release flag
    useEffect(() => {
        localStorage.setItem("library:isNewRelease", isNewRelease.toString());
    }, [isNewRelease]);

    // Sync URL with filters
    useEffect(() => {
        if (isSearchOpened) return;

        const params: { [key: string]: string } = {};

        const ribbonsForUrl = browseFilters.ribbonsIds.filter((id) => id !== 18);
        if (ribbonsForUrl.length) params[FiltersEnum.ribbonsIds] = ribbonsForUrl.join(",");
        if (browseFilters.tagsIds.length) params[FiltersEnum.tags] = browseFilters.tagsIds.join(",");
        if (browseFilters.subscriptionPackageIds.length)
            params[FiltersEnum.packages] = browseFilters.subscriptionPackageIds.join(",");
        if (browseFilters.orderBy && browseFilters.orderBy !== SORT_ENUM.DATE)
            params[FiltersEnum.orderBy] = browseFilters.orderBy;
        if (browseFilters.sortOrder && browseFilters.sortOrder !== SORT_ENUM.DESC)
            params[FiltersEnum.sortOrder] = browseFilters.sortOrder;
        if (browseFilters.searchQuery) params[FiltersEnum.searchText] = browseFilters.searchQuery;

        const newQueryString = getQueryString(params);
        if (newQueryString !== location.search.replace(/^\?/, "")) {
            history.replace({ pathname: location.pathname, search: newQueryString });
        }
    }, [browseFilters, history, location.pathname, location.search, isSearchOpened]);

    // Update access filter when the user switches between My Library / All
    useEffect(() => {
        if (browseFilters.hasAccess !== inMyLibrary) {
            setBrowseFilters((prev) => ({ ...prev, hasAccess: inMyLibrary }));
        }
    }, [inMyLibrary, browseFilters.hasAccess]);

    // Keep types in sync with MAC ribbon selection
    useEffect(() => {
        if (!macRibbonId) return;

        const hasMacRibbon = browseFilters.ribbonsIds.includes(macRibbonId);
        const isMacOnly = browseFilters.types.length === 1 && browseFilters.types[0] === DocumentTypesEnum.Mac;

        if (!isTypeExplicit) {
            if (hasMacRibbon && !isMacOnly) {
                applyPatch({ types: [DocumentTypesEnum.Mac], orderBy: "updateDate" }, true);
            }

            if (!hasMacRibbon && isMacOnly) {
                applyPatch({ types: [DocumentTypesEnum.Publication], orderBy: SORT_ENUM.DATE }, true);
            }
        }
    }, [macRibbonId, browseFilters.ribbonsIds, browseFilters.types, isTypeExplicit, applyPatch]);

    // Reset ordering when leaving MAC ribbon
    useEffect(() => {
        const isMenuAdoptionSelected = macRibbonId ? browseFilters.ribbonsIds.includes(macRibbonId) : false;
        const isMacOnly = browseFilters.types.length === 1 && browseFilters.types[0] === DocumentTypesEnum.Mac;

        if (!isMenuAdoptionSelected && !isMacOnly && browseFilters.orderBy === "updateDate") {
            setBrowseFilters((prev) => ({ ...prev, orderBy: SORT_ENUM.DATE }));
        }
    }, [browseFilters.ribbonsIds, browseFilters.types, browseFilters.orderBy, macRibbonId]);

    /* -------------------------------------------------
     * API
     * -------------------------------------------------*/
    return {
        browseFilters,
        updateBrowseFilters,
        isNewRelease,
        setIsNewRelease,
        macRibbonId
    };
} 